import React, { useState, useEffect } from "react";
import { Navbar, Nav, Container, NavDropdown, Image, Badge, Form } from "react-bootstrap";
import { FaUserCircle, FaBuilding, FaSignOutAlt, FaUser, FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import firebaseApp from "../firebase/credenciales";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

const NavbarNav = ({ usuario, updateUserCompany }) => {
  const [empresaInfo, setEmpresaInfo] = useState(null);
  const [listaEmpresas, setListaEmpresas] = useState([]);
  const navigate = useNavigate();

  const getEmpresasIds = () => {
    const raw =
      usuario?.empresasHabilitadas ??
      usuario?.empresasIds ??
      usuario?.empresasIDs ??
      usuario?.rucs ??
      usuario?.companyIds ??
      [];

    const base = Array.isArray(raw)
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? raw.split(',')
        : [];

    const merged = [...base, usuario?.ruc, usuario?.rucEmpresa];
    return [...new Set(merged.map((v) => String(v || '').trim()).filter(Boolean))];
  };

  // Cargar información de la empresa actual
  useEffect(() => {
    const fetchEmpresaInfo = async () => {
      if (usuario?.ruc) {
        const docRef = doc(db, "company", usuario.ruc);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEmpresaInfo(docSnap.data());
        } else {
          setEmpresaInfo(null);
        }
      } else {
        setEmpresaInfo(null);
      }
    };
    fetchEmpresaInfo();
  }, [usuario?.ruc]);

  // Cargar lista de empresas habilitadas si existen
  useEffect(() => {
    const empresasIds = getEmpresasIds();
    console.log("NavbarNav: Effect triggered. Usuario:", usuario);
    console.log("NavbarNav: Empresas IDs:", empresasIds);

    const fetchEmpresasHabilitadas = async () => {
      if (Array.isArray(empresasIds) && empresasIds.length > 0) {
        console.log("NavbarNav: Fetching details for", empresasIds.length, "companies");
        try {
          const promesas = empresasIds.map(async (ruc) => {
            try {
              const docRef = doc(db, "company", ruc);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                return { ruc, ...docSnap.data() };
              }
              console.warn("NavbarNav: Company not found for RUC", ruc);
              return null;
            } catch (err) {
              console.warn(`NavbarNav: Sin permisos o error al consultar empresa RUC ${ruc}:`, err?.message || err);
              return null;
            }
          });

          const resultados = await Promise.all(promesas);
          const empresasValidas = resultados.filter(emp => emp !== null);
          console.log("NavbarNav: Empresas validas cargadas:", empresasValidas);
          setListaEmpresas(empresasValidas);
        } catch (error) {
          console.warn("NavbarNav: Error fetching companies:", error?.message || error);
          setListaEmpresas([]);
        }
      } else {
        console.log("NavbarNav: No companies to fetch or invalid format");
        setListaEmpresas([]);
      }
    };
    fetchEmpresasHabilitadas();
  }, [usuario]);

  const handleEmpresaChange = (e) => {
    const newRuc = e.target.value;
    if (updateUserCompany) {
      updateUserCompany(newRuc);
      navigate('/');
    }
  };

  const nombreCompleto = usuario
    ? `${usuario.nombre || ""} ${usuario.apellidos || ""}`.trim()
    : "Usuario";
  const rol = usuario?.rol || "Usuario";

  const handleLogout = () => {
    signOut(auth);
  };

  const handleNavigateToProfile = () => {
    navigate('/perfil');
  };

  const handleNavigateToCompany = () => {
    navigate('/company');
  };

  // Si no hay usuario, mostrar una versión simplificada con botón de cerrar sesión
  if (!usuario) {
    return (
      <Navbar
        expand="lg"
        variant="dark"
        bg="dark"
        className="border-bottom border-secondary py-0 shadow"
        style={{ minHeight: 60 }}
      >
        <Container fluid className="px-4">
          <Navbar.Brand className="d-flex align-items-center py-2 gap-2">
            <FaBuilding size={28} className="me-2 text-primary" />
            <span className="fw-bold fs-5 text-white">Mi Boleta</span>
            <Badge bg="secondary" className="ms-2">Administrador</Badge>
          </Navbar.Brand>
          <Nav className="ms-auto align-items-center">
            <Nav.Link
              onClick={handleLogout}
              className="text-danger fw-semibold d-flex align-items-center gap-2"
              style={{ fontSize: "1.1rem" }}
            >
              <FaSignOutAlt />
              Cerrar Sesión
            </Nav.Link>
          </Nav>
        </Container>
      </Navbar>
    );
  }

  return (
    <Navbar
      expand="lg"
      variant="dark"
      bg="dark"
      className="border-bottom border-secondary py-0 shadow"
      style={{ minHeight: 60, background: "linear-gradient(90deg, #232526 0%, #414345 100%)" }}
    >
      <Container fluid className="px-4">
        <Navbar.Brand className="d-flex align-items-center py-2 gap-3">
          {empresaInfo?.logo ? (
            <Image
              src={empresaInfo.logo}
              alt={empresaInfo.nombreComercial}
              height="40"
              className="me-3 bg-white p-1 border border-2 border-primary"
              style={{
                objectFit: "contain",
                maxWidth: 120,
                maxHeight: 40,
                borderRadius: 8,
                background: "#fff"
              }}
            />
          ) : (
            <FaBuilding size={32} className="me-2 text-primary" />
          )}

          <div className="">
            {listaEmpresas.length > 1 ? (
              <div className="d-flex flex-column">
                <Form.Select
                  size="sm"
                  className="fw-bold text-primary border-0 bg-white shadow-sm"
                  style={{
                    cursor: 'pointer',
                    fontSize: '1rem',
                    minWidth: '200px'
                  }}
                  value={usuario?.ruc || ""}
                  onChange={handleEmpresaChange}
                >
                  {listaEmpresas.map((emp) => (
                    <option key={emp.ruc} value={emp.ruc}>
                      {emp.nombreComercial || emp.name || emp.ruc}
                    </option>
                  ))}
                </Form.Select>
                <div className="fw-semibold text-secondary small ps-2 mt-1">
                  {empresaInfo?.name || "Administrador"}
                </div>
              </div>
            ) : (
              <>
                <div className="fw-bold fs-4 text-white text-shadow">
                  {empresaInfo?.nombreComercial || "Mi Boleta"}
                </div>
                <div className="fw-semibold text-secondary small">
                  {empresaInfo?.name || "Administrador"}
                </div>
              </>
            )}
          </div>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="navbar-nav" className="border-0" />

        <Navbar.Collapse id="navbar-nav">
          <Nav className="ms-auto align-items-center gap-2">
            <NavDropdown
              align="end"
              title={
                <div className="d-flex align-items-center gap-2">
                  <FaUserCircle size={32} className="text-primary bg-white rounded-circle p-1 border border-2 border-primary shadow-sm" />
                  <div className="d-none d-lg-block text-white">
                    <div className="fw-semibold mb-0" style={{ fontSize: "1rem" }}>
                      {nombreCompleto}
                    </div>
                    <Badge bg="info" className="text-dark">{rol}</Badge>
                  </div>
                </div>
              }
              id="user-dropdown"
              className="py-0"
              menuVariant="light"
            >
              <div className="px-3 py-3 bg-light border-bottom rounded-top">
                <div className="fw-semibold fs-6 mb-1 text-dark">{nombreCompleto}</div>
                <div className="text-secondary small mb-1">
                  <FaBriefcase className="me-1" />
                  {rol}
                </div>
                {empresaInfo && (
                  <div className="text-primary small d-flex align-items-center">
                    <FaBuilding className="me-1" />
                    {empresaInfo.nombreComercial}
                  </div>
                )}
              </div>

              <NavDropdown.Item
                onClick={handleNavigateToProfile}
                className="px-3 py-2 d-flex align-items-center gap-2"
              >
                <FaUser className="text-secondary" />
                Mi Perfil
              </NavDropdown.Item>

              <NavDropdown.Item
                onClick={handleNavigateToCompany}
                className="px-3 py-2 d-flex align-items-center gap-2"
              >
                <FaBuilding className="text-secondary" />
                Empresa
              </NavDropdown.Item>

              <NavDropdown.Divider />

              <NavDropdown.Item
                onClick={handleLogout}
                className="px-3 py-2 d-flex align-items-center gap-2 text-danger fw-semibold"
              >
                <FaSignOutAlt />
                Cerrar Sesión
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
      <style>{`
        .navbar {
          box-shadow: 0 2px 10px 0 rgba(0,0,0,0.08);
        }
        .text-shadow {
          text-shadow: 1px 1px 4px #00000033;
        }
      `}</style>
    </Navbar>
  );
};

export default NavbarNav;
